import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import api from '../../../services/api';
import { parseTableParams } from '../../../utils/Utils';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import DataTable from '../../../components/ui/DataTable';
import OptionsMenu from '../../../components/ui/OptionsMenu';

const Container = styled.div``;

export default function GateAccessPointsTab({ gateId }) {
  const { t } = useTranslation();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageIndex: 0, pageSize: 10 });
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
  const [deleting, setDeleting] = useState(false);

  const deviceTypeLabel = useCallback((val) => {
    const map = {
      turnstile: t('accessPoint.turnstile'),
      door: t('accessPoint.door'),
      barrier: t('accessPoint.barrier'),
      qr_point: t('accessPoint.qrPoint'),
    };
    return map[val] || val;
  }, [t]);

  useEffect(() => {
    setFetching(true);
    api.get(`/v1/access-points?gateId=${gateId}&pageSize=500`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .catch(() => {})
      .finally(() => setFetching(false));
  }, [gateId, queryParams, refreshTrigger]);

  const baseColumns = useMemo(() => [
    {
      name: 'name', label: t('accessPoint.name'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    {
      name: 'deviceType', label: t('accessPoint.deviceType'),
      options: {
        filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' },
        customBodyRender: (value) => deviceTypeLabel(value) || '-',
      },
    },
    {
      name: 'ipAddress', label: t('accessPoint.ipAddress'),
      options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
  ], [t, deviceTypeLabel]);

  const handleRemoveClick = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, id: row.id, name: row.name });
  }, []);

  const handleRemoveConfirm = useCallback(async () => {
    setDeleting(true);
    try {
      await api.delete(`/v1/access-points/${deleteConfirm.id}`);
      setRefreshTrigger((p) => p + 1);
    } catch { /* interceptor */ }
    setDeleteConfirm({ isOpen: false, id: null, name: '' });
    setDeleting(false);
  }, [deleteConfirm.id]);

  const actionOptions = useMemo(() => [
    { type: 'delete', label: t('accessPoint.delete'), onClick: handleRemoveClick },
  ], [t, handleRemoveClick]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions', label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <OptionsMenu actions={actionOptions} row={row} />;
        },
      },
    },
  ], [baseColumns, actionOptions, data]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const handleSave = async (formData) => {
    try {
      // Okuyucu yönü: 'none' gözcüsü → null (kapının yönünden / son hareketten tahmin edilir).
      const direction = formData?.direction === 'entry' || formData?.direction === 'exit' ? formData.direction : null;
      await api.post('/v1/access-points', { ...formData, direction, gateId });
      setDrawerOpen(false);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch { return false; }
  };

  const readerDirectionOptions = useMemo(() => [
    { id: 'none', name: t('accessPoint.directionNone') },
    { id: 'entry', name: t('accessPoint.directionEntry') },
    { id: 'exit', name: t('accessPoint.directionExit') },
  ], [t]);

  const deviceTypeOptions = useMemo(() => [
    { id: 'turnstile', name: t('accessPoint.turnstile') },
    { id: 'door', name: t('accessPoint.door') },
    { id: 'barrier', name: t('accessPoint.barrier') },
    { id: 'qr_point', name: t('accessPoint.qrPoint') },
  ], [t]);

  const schema = useMemo(() => [
    { name: 'name', label: t('accessPoint.name'), type: 'text', grid: 12, required: true },
    { name: 'ipAddress', label: t('accessPoint.ipAddress'), type: 'text', grid: 12 },
    {
      name: 'deviceType', label: t('accessPoint.deviceType'), type: 'select', grid: 12, required: true,
      options: deviceTypeOptions, idKey: 'id', labelKey: 'name',
    },
    {
      name: 'direction', label: t('accessPoint.direction'), type: 'select', grid: 12,
      options: readerDirectionOptions, idKey: 'id', labelKey: 'name', defaultValue: 'none',
    },
    { name: 'latitude', label: t('accessPoint.latitude'), type: 'number', grid: 6, description: t('accessPoint.latitudeHint') },
    { name: 'longitude', label: t('accessPoint.longitude'), type: 'number', grid: 6, description: t('accessPoint.longitudeHint') },
    { name: 'geoFenceRadiusMeters', label: t('accessPoint.geoFenceRadius'), type: 'number', grid: 12, description: t('accessPoint.geoFenceRadiusHint') },
  ], [t, deviceTypeOptions, readerDirectionOptions]);

  return (
    <Container>
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('accessPoint.deleteTitle')}
        message={t('accessPoint.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleRemoveConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
        loading={deleting}
      />

      {useMemo(() => (
        <DataTable
          title={t('accessPoint.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord
          newButtonLabel={t('accessPoint.new')}
          onNewButtonClick={() => setDrawerOpen(true)}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination,
        filterList, handleFilterConfirm, handleDataStateChange,
      ])}

      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('accessPoint.new')}
        schema={schema}
        onSave={handleSave}
      />
    </Container>
  );
}
